import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, 
  CheckCircle, Clock, RefreshCw, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../context/LanguageContext';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_CONFIG = {
  exceeded: {
    color: 'red',
    icon: AlertTriangle,
    bg: 'bg-red-50 dark:bg-red-950/20',
    border: 'border-red-200',
    text: 'text-red-600'
  },
  warning: {
    color: 'amber',
    icon: Clock,
    bg: 'bg-amber-50 dark:bg-amber-950/20',
    border: 'border-amber-200',
    text: 'text-amber-600'
  },
  caution: {
    color: 'orange',
    icon: TrendingUp,
    bg: 'bg-orange-50 dark:bg-orange-950/20',
    border: 'border-orange-200',
    text: 'text-orange-600'
  },
  ok: {
    color: 'green',
    icon: CheckCircle,
    bg: 'bg-green-50 dark:bg-green-950/20',
    border: 'border-green-200',
    text: 'text-green-600'
  }
};

const CATEGORY_NAMES_FR = {
  logement: 'Logement',
  transport: 'Transport',
  alimentation: 'Alimentation',
  abonnements: 'Abonnements',
  shopping: 'Shopping',
  loisirs: 'Loisirs',
  sante: 'Santé',
  epargne: 'Épargne',
  divers: 'Divers',
};

export default function PredictionsPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    loadPredictions();
  }, []);

  const loadPredictions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/predictions/budget');
      setPredictions(res.data.predictions || []);
      setSummary(res.data.summary);
      setAlerts(res.data.alerts || []);
    } catch (error) {
      toast.error(t('loading_error'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  {t('ai_predictions')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('anticipate_spending')}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadPredictions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('refresh')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Card */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-gradient-to-br from-primary/10 to-orange-100 dark:from-primary/20 dark:to-orange-900/20">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('total_budget')}
                    </p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_budget)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('spent')}
                    </p>
                    <p className="text-2xl font-bold">{formatCurrency(summary.total_spent)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('predicted_end_month')}
                    </p>
                    <p className={`text-2xl font-bold ${summary.total_predicted > summary.total_budget ? 'text-red-500' : 'text-green-600'}`}>
                      {formatCurrency(summary.total_predicted)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {t('daily_budget_remaining')}
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(summary.daily_budget_recommended)}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-white/50 dark:bg-black/20 rounded-lg">
                  <p className="text-sm">
                    <span className="font-medium">{summary.days_remaining}</span>
                    {' '}{t('days_remaining_month')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Calculation Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
        >
          <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-blue-700 dark:text-blue-400">
                <Zap className="w-4 h-4" />
                {t('how_predictions_calculated')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                  <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                    📊 {t('daily_rate')}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('daily_rate_explanation')}
                  </p>
                  {summary && (
                    <p className="text-xs mt-1 font-mono bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                      {formatCurrency(summary.total_spent)} ÷ {new Date().getDate()} = {formatCurrency(summary.total_spent / new Date().getDate())}/jour
                    </p>
                  )}
                </div>
                
                <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                  <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                    🎯 {t('end_month_forecast')}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('end_month_forecast_explanation')}
                  </p>
                  {summary && (
                    <p className="text-xs mt-1 font-mono bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                      {formatCurrency(summary.total_spent)} + ({formatCurrency(summary.total_spent / new Date().getDate())} × {summary.days_remaining}) = {formatCurrency(summary.total_predicted)}
                    </p>
                  )}
                </div>
                
                <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                  <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                    💰 {t('recommended_daily_budget')}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('recommended_daily_budget_explanation')}
                  </p>
                  {summary && (
                    <p className="text-xs mt-1 font-mono bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                      ({formatCurrency(summary.total_budget)} - {formatCurrency(summary.total_spent)}) ÷ {summary.days_remaining} = {formatCurrency(summary.daily_budget_recommended)}/jour
                    </p>
                  )}
                </div>
                
                <div className="p-3 bg-white/60 dark:bg-black/20 rounded-lg">
                  <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">
                    ℹ️ {t('what_is_excluded')}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('what_is_excluded_explanation')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  {t('urgent_alerts')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-medium">
                        {t(`cat_${alert.category}`) || alert.category}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ? alert.message_fr : language === 'pt' ? (alert.message_pt || alert.message_en) : alert.message_en}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Predictions List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('predictions_by_category')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {predictions.length > 0 ? (
                predictions.map((pred, idx) => {
                  const config = STATUS_CONFIG[pred.status] || STATUS_CONFIG.ok;
                  const Icon = config.icon;
                  
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * idx }}
                      className={`p-4 rounded-xl border ${config.border} ${config.bg}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-5 h-5 ${config.text}`} />
                          <span className="font-bold">
                            {t(`cat_${pred.category}`) || pred.category}
                          </span>
                        </div>
                        <span className={`text-sm font-medium ${config.text}`}>
                          {pred.status === 'exceeded' ? '🚨' : pred.status === 'warning' ? '⚠️' : pred.status === 'caution' ? '📈' : '✅'}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>{t('spent')}: {formatCurrency(pred.spent)}</span>
                          <span>{t('budgets')}: {formatCurrency(pred.budget)}</span>
                        </div>
                        
                        <Progress 
                          value={Math.min((pred.spent / pred.budget) * 100, 100)} 
                          className="h-2"
                        />
                        
                        <p className={`text-sm ${config.text}`}>
                          {language === 'fr' ? pred.message_fr : language === 'pt' ? (pred.message_pt || pred.message_en) : pred.message_en}
                        </p>
                        
                        {pred.days_until_exceeded && (
                          <p className="text-xs text-muted-foreground">
                            {t('current_rate')}: {formatCurrency(pred.daily_rate)}{t('per_day')}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">
                    {t('no_predictions_available')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('create_budgets_to_see_predictions')}
                  </p>
                  <Button className="mt-4" onClick={() => navigate('/budgets')}>
                    {t('create_budget')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                💡 {t('predictions_tip')}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
